import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  message,
} from "antd";
import { Spool, SpoolHolding } from "../../types/spool.type";
import { Currency } from "../../types/currency.type";
import { spoolApi } from "../../api/spoolApi";
import { useExchangeRate } from "../../contexts/exchangeRate.context";
import { useAuth } from "../../contexts/auth.context";
import { canEditProduces } from "../../utils/roleUtils";

const { Option } = Select;

const Spools: React.FC = () => {
  const { authState } = useAuth();
  const { exchangeRate } = useExchangeRate();
  const canManage = canEditProduces(authState.user?.role);

  const [spools, setSpools] = useState<Spool[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Spool | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [form] = Form.useForm();

  const [holdersOpen, setHoldersOpen] = useState(false);
  const [holdersSpool, setHoldersSpool] = useState<Spool | null>(null);
  const [holdings, setHoldings] = useState<SpoolHolding[]>([]);
  const [holdingsLoading, setHoldingsLoading] = useState(false);

  const fetchSpools = async () => {
    setLoading(true);
    try {
      const data = await spoolApi.getSpools();
      setSpools(data);
    } catch (error) {
      message.error("Ошибка при загрузке катушек");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSpools();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ currency: Currency.US_DOLLAR, stock: 0 });
    setModalOpen(true);
  };

  const openEdit = (spool: Spool) => {
    setEditing(spool);
    form.setFieldsValue({
      name: spool.name,
      price: spool.price.USD,
      currency: Currency.US_DOLLAR,
      stock: spool.stock,
      isActive: spool.isActive,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (values: any) => {
    if (!exchangeRate) {
      message.error("Не удалось получить курс. Попробуйте позже.");
      return;
    }
    setConfirmLoading(true);
    try {
      const payload = {
        name: values.name,
        price: values.price,
        currency: values.currency,
        exchangeRate,
        stock: values.stock,
      };
      if (editing) {
        await spoolApi.updateSpool(editing._id, { ...payload, isActive: values.isActive });
        message.success("Катушка обновлена");
      } else {
        await spoolApi.createSpool(payload);
        message.success("Катушка создана");
      }
      setModalOpen(false);
      fetchSpools();
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Ошибка при сохранении");
    } finally {
      setConfirmLoading(false);
    }
  };

  const openHolders = async (spool: Spool) => {
    setHoldersSpool(spool);
    setHoldersOpen(true);
    setHoldingsLoading(true);
    try {
      const data = await spoolApi.getHoldings(spool._id);
      setHoldings(data);
    } catch (error) {
      message.error("Ошибка при загрузке держателей катушек");
    } finally {
      setHoldingsLoading(false);
    }
  };

  const handleDelete = async (spool: Spool) => {
    try {
      await spoolApi.deleteSpool(spool._id);
      message.success("Катушка удалена");
      fetchSpools();
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Ошибка при удалении");
    }
  };

  const columns = [
    { title: "Название", dataIndex: "name", key: "name" },
    {
      title: "Цена (USD)",
      dataIndex: ["price", "USD"],
      key: "priceUSD",
      render: (v: number) => v?.toLocaleString("ru-RU"),
    },
    {
      title: "Цена (UZS)",
      dataIndex: ["price", "UZS"],
      key: "priceUZS",
      render: (v: number) => v?.toLocaleString("ru-RU"),
    },
    { title: "На складе", dataIndex: "stock", key: "stock" },
    {
      title: "У клиентов",
      dataIndex: "withClients",
      key: "withClients",
      render: (v: number, spool: Spool) =>
        v > 0 ? (
          <Button type="link" style={{ padding: 0 }} onClick={() => openHolders(spool)}>
            {v} — кто держит
          </Button>
        ) : (
          0
        ),
    },
    {
      title: "Статус",
      dataIndex: "isActive",
      key: "isActive",
      render: (active: boolean) =>
        active ? <Tag color="green">Активна</Tag> : <Tag color="default">Скрыта</Tag>,
    },
    ...(canManage
      ? [
          {
            title: "",
            key: "actions",
            render: (_: any, spool: Spool) => (
              <Space>
                <Button type="link" onClick={() => openEdit(spool)}>
                  Изменить
                </Button>
                <Popconfirm
                  title="Удалить катушку?"
                  okText="Да"
                  cancelText="Нет"
                  onConfirm={() => handleDelete(spool)}
                >
                  <Button type="link" danger>
                    Удалить
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 16, justifyContent: "space-between", width: "100%" }}>
        <h2 style={{ margin: 0 }}>Катушки</h2>
        {canManage && (
          <Button type="primary" onClick={openCreate}>
            Добавить катушку
          </Button>
        )}
      </Space>

      <Table
        columns={columns as any}
        dataSource={spools}
        rowKey={(r) => r._id}
        loading={loading}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title={editing ? "Изменить катушку" : "Добавить катушку"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Название (вид катушки)"
            rules={[{ required: true, message: "Введите название" }]}
          >
            <Input placeholder="Например: Катушка 10" />
          </Form.Item>

          <Form.Item
            name="price"
            label="Залоговая цена"
            rules={[{ required: true, message: "Введите цену" }]}
          >
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>

          <Form.Item
            name="currency"
            label="Валюта цены"
            rules={[{ required: true, message: "Выберите валюту" }]}
          >
            <Select>
              {Object.values(Currency).map((c) => (
                <Option key={c} value={c}>
                  {c}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="stock" label="Остаток на складе">
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>

          {editing && (
            <Form.Item name="isActive" label="Активна" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}

          <div style={{ color: "#888" }}>
            Цена будет пересчитана в обе валюты по текущему курсу{exchangeRate ? ` (${exchangeRate})` : ""}.
          </div>
        </Form>
      </Modal>

      <Modal
        title={`Кто держит катушку${holdersSpool ? `: ${holdersSpool.name}` : ""}`}
        open={holdersOpen}
        onCancel={() => setHoldersOpen(false)}
        footer={[
          <Button key="close" onClick={() => setHoldersOpen(false)}>
            Закрыть
          </Button>,
        ]}
        width={640}
      >
        <Table
          size="small"
          loading={holdingsLoading}
          dataSource={holdings}
          rowKey={(r) => r._id}
          pagination={false}
          columns={[
            { title: "Клиент", dataIndex: "clientName", key: "clientName" },
            { title: "Телефон", dataIndex: "clientPhone", key: "clientPhone" },
            { title: "Кол-во", dataIndex: "quantity", key: "quantity" },
            {
              title: "Залог",
              dataIndex: "depositAmount",
              key: "depositAmount",
              render: (v: number, r: SpoolHolding) =>
                `${(v || 0).toLocaleString("ru-RU")} ${r.currency}`,
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default Spools;
